import { Text, View, ViewStyle } from 'react-native';
import TextLato from '../text/text-lato/TextLato';
import { observer } from 'mobx-react-lite';
import { ILookupOptionModel } from 'src/store/_models/lookup';
import { useStore } from 'src/contexts/StoreContext';
import { LookupChoice } from '../lookup-choice/LookupChoise';
import { useVerticalScrollView } from 'src/hooks/useVerticalScrollView';
import { useCallback, useEffect, useState } from 'react';
import Datadog from 'src/_shared/datadog/index.web';
import { DATADOG_CUSTOM_ACTIONS } from '../../_shared/datadog/DatadogCustomActions';
import amplitude from 'src/_shared/amplitude';
import { useLookupOptions } from 'src/hooks/useLookupOptions';
import { ICON_NAMES } from '../icon/icon.helper';
import { IUserImageModel } from '../../store/_models/userImage';
import { CanvasBorderType } from '@photosi/expo-photosi-sdk/build/ImageEditor';
import { IProductModel } from '../../store/_models/product';
import { LookupCodes, LookupCodesWithVisibleDescriptions } from '../../constants/lookup.constants';
import { styles } from './BottomSheetLookupOptions.styles';
import { useGridInfo } from '../../hooks/useGridInfo';
import { configurationTestID, lookupChoiceTestID } from 'src/helpers/testIDs.helper';
import { FocusGroups } from '../focus-provider/FocusProvider.constants';
import { CircularButton } from '../buttons/circular-button/CircularButton';
import { CIRCULAR_BUTTON_SIZES, CIRCULAR_BUTTON_TYPES } from '../buttons/circular-button/CircularButton.constants';
import { TestIDs } from 'src/constants/testIDs.constants';
import { AnalyticsEvents } from 'src/_shared/analyticsEvents';
import { BottomSheetScrollView } from '@gorhom/bottom-sheet';
import { getLookupCodeLabelFor } from 'src/_shared/utils/getLookupCodeLabelFor';

export const BottomSheetLookupOptions = observer(({ bsStyle }: { bsStyle: ViewStyle }) => {
  const {
    i18n,
    project: { setProductConfigurationId, activeProduct, allProducts },
    bottomSheet: { bottomSheetVisible, closeBottomSheet, bottomSheetLookup, bottomSheetTitle, removeBottomSheetLookup },
  } = useStore();

  const lookupCode = bottomSheetTitle.toLocaleLowerCase();

  const bottomSheetLookupOptions = bottomSheetLookup?.options || [];

  const { onGridContainerLayout, onGridItemLayout, missingElementCount, gridItemWidth } =
    useGridInfo(bottomSheetLookupOptions);

  const { scrollVierRef, getScrollViewElementTestId, scrollToIndex } = useVerticalScrollView();

  const [activeLookupOptionId, setActiveLookupOptionId] = useState<string>('');
  // const [isChoiceUpdated, setIsChoiceUpdated] = useState<boolean>(false);

  useEffect(() => {
    if (bottomSheetVisible && bottomSheetLookupOptions) {
      setActiveLookupOptionId(
        bottomSheetLookupOptions.find((option: ILookupOptionModel) => option.isSelected)?.id || '',
      );
      const selectedIndex = bottomSheetLookupOptions.findIndex((option: ILookupOptionModel) => option.isSelected);
      // waiting for BottomSheet to open
      setTimeout(() => {
        scrollToIndex(selectedIndex);
        Datadog.endMeasure(DATADOG_CUSTOM_ACTIONS.configuration_options_loading_time, {
          activeLookup: activeLookupOptionId,
        });
      }, 200);
    }
  }, [bottomSheetVisible]);

  const onPressLookup = useCallback(
    ({ isCustom, lookupCode, value, productConfigurationId, id }: ILookupOptionModel, applyAll?: boolean) => {
      const isKitTypeLookup = lookupCode === LookupCodes.KIT_TYPE;
      if (isKitTypeLookup) {
        setProductConfigurationId(value);
        removeBottomSheetLookup();
      }
      setActiveLookupOptionId(id);
      if (isCustom) {
        if (lookupCode === LookupCodes.BORDER_TYPE) {
          activeProduct?.userImages.forEach((userImage: IUserImageModel) => {
            userImage.setBorder(value as CanvasBorderType);
          });
        }
        // add here more custom actions...
      } else if (productConfigurationId && activeProduct) {
        if (applyAll) {
          Promise.allSettled(
            allProducts.map((product: IProductModel) => product.applyLookup(productConfigurationId)),
          ).then(() => {
            closeBottomSheet();
          });
        } else {
          activeProduct.applyLookup(productConfigurationId).then(() => {
            closeBottomSheet();
          });
        }
      }
      // setIsChoiceUpdated(false);
    },
    [activeProduct, activeLookupOptionId],
  );

  // const handleApplyAllClick = async () => {
  //   const selectedLookup = bottomSheetLookupOptions.find(item => item.id === activeLookupOptionId);
  //
  //   if (selectedLookup) {
  //     onPressLookup(selectedLookup, true);
  //   }
  // };
  // const applyAllPriceDifference = () => {
  //   const selectedLookupPrice = Number(
  //     bottomSheetLookupOptions
  //       ?.find(item => item.id === activeLookupOptionId)
  //       ?.formattedPrice?.replace(/[^\d.]/g, '') || 0,
  //   );
  //
  //   const nextPrice =
  //     totalNumberOfProducts * selectedLookupPrice - (activeProject?.original || activeProject?.estimate);
  //   amplitude.trackEvent(AnalyticsEvents.personalize.lookupSelect, {
  //     action: 'applyAll',
  //     lookupId: activeLookupOptionId,
  //     lookupCode: lookupCode,
  //   });
  //   const rounded = Math.round(nextPrice * 100) / 100;
  //   if (Math.abs(rounded) < 0.01) return '+0,00';
  //
  //   return rounded > 0 ? `+${rounded.toFixed(2)}` : rounded.toFixed(2);
  // };

  // const handleUpdateChoice = (selectedOption: ILookupOptionModel) => {
  //   // do not show apply button when clicking on the already selected option
  //   const areAllProductsEqual = allProducts
  //     .flatMap((item: ProductModelInstance) => item.lookups)
  //     .filter((item: ILookupModel) => item.code === activeLookupOptionId)
  //     .flatMap((item: ILookupModel) => item.options)
  //     .some((item: ILookupOptionModel) => item.id === selectedOption.id && !item.selected);
  //   setIsChoiceUpdated(areAllProductsEqual);
  // };

  const { isPriceVisible } = useLookupOptions(bottomSheetLookupOptions);

  return (
    <BottomSheetScrollView ref={scrollVierRef} contentContainerStyle={bsStyle}>
      <View style={styles.titleContainer}>
        <TextLato style={styles.title}>{i18n.t(`lookups:${getLookupCodeLabelFor(lookupCode)}`)}</TextLato>
        <CircularButton
          onPress={async () => closeBottomSheet()}
          accessible={true}
          accessibilityLabel={i18n.t('actions:close_x', { x: i18n.t(`lookups:${getLookupCodeLabelFor(lookupCode)}`) })}
          accessibilityRole="button"
          iconName={ICON_NAMES.Close}
          iconStyle={styles.icon}
          size={CIRCULAR_BUTTON_SIZES.SMALL}
          type={CIRCULAR_BUTTON_TYPES.ONLY_ICON}
          testID={configurationTestID(lookupCode, TestIDs.CLOSE)}
          focusId={configurationTestID(lookupCode, TestIDs.CLOSE)}
          focusOrder={0}
          focusGroup={FocusGroups.BOTTOM_SHEET}
        />
      </View>
      {LookupCodesWithVisibleDescriptions.includes(lookupCode as LookupCodes) && (
        <Text style={{ marginBottom: 16 }}>{i18n.t(`lookups:${lookupCode}-description`)}</Text>
      )}
      <View style={styles.container} onLayout={onGridContainerLayout}>
        {bottomSheetLookupOptions.map((option: ILookupOptionModel, index: number) => {
          const testID = lookupChoiceTestID(lookupCode, index);
          return (
            <View
              key={option.id}
              testID={getScrollViewElementTestId(index)}
              onLayout={index === 0 ? onGridItemLayout : undefined}
            >
              <LookupChoice
                title={i18n.t(option.titleKey)}
                previewImageUrl={option.previewImageUrl}
                formattedPrice={option.formattedPrice}
                testID={testID}
                onPress={async () => {
                  onPressLookup(option);
                  // handleUpdateChoice(option);
                  amplitude.trackEvent(AnalyticsEvents.personalize.lookupSelect, {
                    action: 'apply',
                    lookupId: option.id,
                    lookupcode: lookupCode,
                  });
                }}
                isActive={activeLookupOptionId ? option.id === activeLookupOptionId : option.selected}
                isSuboptimal={option?.isSuboptimal}
                isPriceVisible={isPriceVisible}
                focusId={testID}
                focusOrder={index}
                focusGroup={FocusGroups.LOOKUP_CHOICE}
                disabled={option.disabled}
              />
            </View>
          );
        })}
        {/* The hidden elements fill the last row of the grid */}
        {Array.from({ length: missingElementCount }, (_, index) => (
          <View
            key={'hidden-element' + index}
            style={{
              width: gridItemWidth,
            }}
          ></View>
        ))}
      </View>
    </BottomSheetScrollView>
  );
});

BottomSheetLookupOptions.displayName = 'BottomSheetLookupOptions';
